import { CheckIcon, CopyIcon } from 'lucide-react'
import { useState } from 'react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { copyTextToClipboard } from '@renderer/utils/clipboard'

export const QUOTE_HEADER_CLASS = '-mb-1.5 mt-3 flex items-center justify-between gap-2'

interface QuoteHeaderContentProps {
  label: string
  /** Markdown of the quote this header labels, read at click time; `null` when there is none. */
  resolveQuote: () => string | null
}

/** The label and copy button of a quote header, shared by the Tiptap node view and the read-only renderer. */
export function QuoteHeaderContent({ label, resolveQuote }: QuoteHeaderContentProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    const markdown = resolveQuote()
    if (markdown == null) {
      console.warn('Copy quote: no blockquote follows this header, nothing copied.')
      return
    }
    if (!(await copyTextToClipboard(markdown))) return
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    AmplitudeRenderer.trackEvent('Staged Action', { action: 'copy quote' })
  }

  return (
    <>
      <span className="text-foreground-secondary min-w-0 truncate text-xs font-medium">{label}</span>
      <button
        type="button"
        aria-label={label ? `Copy ${label}` : 'Copy quote'}
        className="text-foreground-secondary flex shrink-0 cursor-pointer items-center gap-1 bg-transparent text-xs font-medium transition-transform active:scale-[0.96]"
        // Prevent ProseMirror from capturing the event before React handles it.
        onMouseDown={(event) => {
          event.preventDefault()
          event.stopPropagation()
        }}
        onClick={(event) => {
          event.stopPropagation()
          handleCopy().catch(console.error)
        }}
      >
        {copied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </>
  )
}
